import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { CheckCircle2, Pencil, Plus, Trash2 } from 'lucide-react'

import type { Weighing } from '@/lib/api/weighings-api'
import type { WeighingFormValues } from '@/lib/schemas/weighing-schemas'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { GrowerSearchSelect } from '@/components/growers/grower-search-select'
import { useAuth } from '@/hooks/use-auth'
import { useTeaTypes } from '@/hooks/use-tea-types-query'
import {
    useApproveWeighing,
    useCreateWeighing,
    useDeleteWeighing,
    useUpdateWeighing,
    useWeighings,
} from '@/hooks/use-weighings-query'
import { m } from '@/lib/paraglide/messages'
import { createWeighingSchema } from '@/lib/schemas/weighing-schemas'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/weighings',
)({
    component: WeighingsPage,
})

const ALL = 'all'

function WeighingsPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('weighings.write')
    const canDelete = hasPermission('weighings.delete')
    const canApprove = hasPermission('weighings.approve')

    const [growerFilter, setGrowerFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState(ALL)
    const [editing, setEditing] = useState<Weighing | null>(null)
    const [creating, setCreating] = useState(false)

    const isApprovedFilter =
        statusFilter === 'approved'
            ? true
            : statusFilter === 'unapproved'
              ? false
              : undefined

    const { data } = useWeighings({
        per_page: 100,
        grower_id: growerFilter || undefined,
        is_approved: isApprovedFilter,
    })
    const rows = data?.data ?? []

    const { data: teaTypesData } = useTeaTypes({ per_page: 200 })
    const teaTypes = teaTypesData?.data ?? []

    const teaTypeName = useMemo(() => {
        const map = new Map<string, string>()
        for (const t of teaTypes) map.set(t.id, t.name)
        return map
    }, [teaTypes])

    const createMut = useCreateWeighing()
    const updateMut = useUpdateWeighing(editing?.id ?? '')
    const deleteMut = useDeleteWeighing()
    const approveMut = useApproveWeighing()

    const schema = createWeighingSchema()
    const form = useForm<WeighingFormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            grower_id: '',
            tea_type_id: '',
            weigh_date: '',
            quantity: 0,
            pattern: '',
            ballot_returned: false,
            is_active: true,
        },
    })

    const isOpen = creating || editing !== null

    function openCreate() {
        form.reset({
            grower_id: '',
            tea_type_id: '',
            weigh_date: new Date().toISOString().slice(0, 10),
            quantity: 0,
            pattern: '',
            ballot_returned: false,
            is_active: true,
        })
        setCreating(true)
    }

    function openEdit(w: Weighing) {
        form.reset({
            grower_id: w.grower_id,
            tea_type_id: w.tea_type_id,
            weigh_date: w.weigh_date.slice(0, 10),
            quantity: w.quantity,
            pattern: w.pattern ?? '',
            ballot_returned: w.ballot_returned,
            is_active: w.is_active,
        })
        setEditing(w)
    }

    function closeDialog() {
        setCreating(false)
        setEditing(null)
        form.reset()
    }

    function onSubmit(values: WeighingFormValues) {
        const payload = {
            grower_id: values.grower_id,
            tea_type_id: values.tea_type_id,
            weigh_date: values.weigh_date,
            quantity: values.quantity,
            pattern: values.pattern || null,
            ballot_returned: values.ballot_returned,
            is_active: values.is_active,
        }
        if (editing) {
            updateMut.mutate(payload, { onSuccess: closeDialog })
        } else {
            createMut.mutate(payload, { onSuccess: closeDialog })
        }
    }

    function handleDelete(w: Weighing) {
        if (!confirm(m.weighing_delete_confirm())) return
        deleteMut.mutate(w.id)
    }

    function handleApprove(w: Weighing) {
        if (!confirm(m.weighing_approve_confirm())) return
        approveMut.mutate(w.id)
    }

    const isSaving = createMut.isPending || updateMut.isPending

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">{m.weighing_title()}</h1>
                {canWrite && (
                    <Button onClick={openCreate}>
                        <Plus className="mr-2 h-4 w-4" />
                        {m.weighing_new()}
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="flex gap-3 flex-wrap">
                <GrowerSearchSelect
                    value={growerFilter || undefined}
                    onChange={setGrowerFilter}
                    placeholder={m.weighing_filter_all_growers()}
                    allowClear
                    className="w-48"
                />

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48">
                        <SelectValue
                            placeholder={m.weighing_filter_all_status()}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>
                            {m.weighing_filter_all_status()}
                        </SelectItem>
                        <SelectItem value="approved">
                            {m.weighing_filter_approved()}
                        </SelectItem>
                        <SelectItem value="unapproved">
                            {m.weighing_filter_unapproved()}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.weighing_grower()}</TableHead>
                        <TableHead>{m.weighing_tea_type()}</TableHead>
                        <TableHead>{m.weighing_date()}</TableHead>
                        <TableHead>{m.weighing_quantity()}</TableHead>
                        <TableHead>{m.weighing_ballot_returned()}</TableHead>
                        <TableHead>{m.weighing_is_approved()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((w) => (
                        <TableRow key={w.id}>
                            <TableCell>
                                {w.grower_name || '—'}
                            </TableCell>
                            <TableCell>
                                {teaTypeName.get(w.tea_type_id) ??
                                    w.tea_type_id}
                            </TableCell>
                            <TableCell>{w.weigh_date.slice(0, 10)}</TableCell>
                            <TableCell>{w.quantity}</TableCell>
                            <TableCell>
                                <Badge
                                    variant={
                                        w.ballot_returned
                                            ? 'default'
                                            : 'secondary'
                                    }
                                >
                                    {w.ballot_returned
                                        ? m.common_yes()
                                        : m.common_no()}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge
                                    variant={
                                        w.is_approved ? 'default' : 'secondary'
                                    }
                                >
                                    {w.is_approved
                                        ? m.weighing_filter_approved()
                                        : m.weighing_filter_unapproved()}
                                </Badge>
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                                {canApprove && !w.is_approved && (
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleApprove(w)}
                                        title={m.weighing_approve()}
                                    >
                                        <CheckCircle2 className="h-4 w-4" />
                                    </Button>
                                )}
                                {canWrite && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openEdit(w)}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                )}
                                {canDelete && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDelete(w)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Create / Edit dialog */}
            <Dialog
                open={isOpen}
                onOpenChange={(o) => {
                    if (!o) closeDialog()
                }}
            >
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? m.weighing_edit() : m.weighing_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <form
                        id="weighing-form"
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-4"
                    >
                        {/* Grower select */}
                        <div className="space-y-1">
                            <Label>{m.weighing_grower()}</Label>
                            <GrowerSearchSelect
                                value={form.watch('grower_id')}
                                onChange={(v) =>
                                    form.setValue('grower_id', v, {
                                        shouldValidate: true,
                                    })
                                }
                                placeholder={m.weighing_select_grower()}
                                fallbackLabel={editing?.grower_name}
                            />
                            {form.formState.errors.grower_id && (
                                <p className="text-destructive text-sm">
                                    {form.formState.errors.grower_id.message}
                                </p>
                            )}
                        </div>

                        {/* Tea type select */}
                        <div className="space-y-1">
                            <Label>{m.weighing_tea_type()}</Label>
                            <Select
                                value={form.watch('tea_type_id')}
                                onValueChange={(v) =>
                                    form.setValue('tea_type_id', v, {
                                        shouldValidate: true,
                                    })
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue
                                        placeholder={m.weighing_select_tea_type()}
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {teaTypes.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {form.formState.errors.tea_type_id && (
                                <p className="text-destructive text-sm">
                                    {form.formState.errors.tea_type_id.message}
                                </p>
                            )}
                        </div>

                        {/* Weigh date */}
                        <div className="space-y-1">
                            <Label>{m.weighing_date()}</Label>
                            <Input
                                type="date"
                                {...form.register('weigh_date')}
                            />
                            {form.formState.errors.weigh_date && (
                                <p className="text-destructive text-sm">
                                    {form.formState.errors.weigh_date.message}
                                </p>
                            )}
                        </div>

                        {/* Quantity */}
                        <div className="space-y-1">
                            <Label>{m.weighing_quantity()}</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                {...form.register('quantity')}
                            />
                            {form.formState.errors.quantity && (
                                <p className="text-destructive text-sm">
                                    {form.formState.errors.quantity.message}
                                </p>
                            )}
                        </div>

                        {/* Pattern */}
                        <div className="space-y-1">
                            <Label>{m.weighing_pattern()}</Label>
                            <Input {...form.register('pattern')} />
                        </div>

                        {/* Ballot returned */}
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={form.watch('ballot_returned')}
                                onCheckedChange={(v) =>
                                    form.setValue('ballot_returned', v)
                                }
                            />
                            <Label>{m.weighing_ballot_returned()}</Label>
                        </div>

                        {/* Is active */}
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={form.watch('is_active')}
                                onCheckedChange={(v) =>
                                    form.setValue('is_active', v)
                                }
                            />
                            <Label>{m.common_is_active()}</Label>
                        </div>
                    </form>
                    <DialogFooter>
                        <Button
                            form="weighing-form"
                            type="submit"
                            disabled={isSaving}
                        >
                            {m.weighing_save()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
